import { API } from "@swivl-mono/api-helper";
import { VarsHelper } from "@swivl-mono/helpers";
import { LocationVarInterface, LocationVarKey, VarDef, VarType } from "@swivl-mono/types";
import { Tooltip } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { GrRevert } from "react-icons/gr";

import { Member } from "../../../../../Models/Member/Member.Current";
import { InheritancePill, ScheduleScopeKind } from "./InheritancePill";
import { CardEditAction, CardSaveActions, ScheduleCard } from "./ScheduleCard";
import { PageScope } from "./Scope";
import { TimezoneField } from "./TimezoneField";

interface LocationTimezoneSectionProps {
  scope: PageScope;
  /** Fires with the current value on every load, even if the operator never edits. */
  onValueChange?: (value: string) => void;
  /** Fires after the operator explicitly saves a value here. */
  onSaved?: () => void;
}

// Built-in default shown when nothing's set anywhere; matches the server's findCascaded.
const TIMEZONE_DEFAULT = VarDef[VarsHelper.varKeyForType(VarType.Location, LocationVarKey.CortexFacilityTimezone)]?.defaultValue ?? "";

interface ResolvedTimezone {
  ownerScope: ScheduleScopeKind | undefined;
  ownerRow: LocationVarInterface | null;
  value: string;
  /** What the value would fall back to if this scope's row were deleted. */
  parentValue: string;
}

/**
 * Timezone editor for a single scope (org / group / location). Cascades
 * org → group → location like every other inheritance-aware var, so an org that
 * mostly runs in one zone doesn't have to set it on every location.
 *
 * Editing an inherited value needs no separate "Override" step — the field is
 * seeded from what's inherited, and saving writes a row at this scope.
 *
 * Storage is the existing `CortexFacilityTimezone` LocationVar, no new schema.
 * The bot's `ScheduleService.getLocationTimezone` reads via `findCascaded`, so
 * the runtime resolves the same zone this page shows.
 */
export function LocationTimezoneSection({ scope, onValueChange, onSaved }: LocationTimezoneSectionProps): React.ReactElement | null {
  const orgId = Member.orgId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ResolvedTimezone>({ ownerScope: undefined, ownerRow: null, value: "", parentValue: "" });
  const [draft, setDraft] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async (): Promise<void> => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const all = await API.LocationVars.getAllVariablesForKey(orgId, LocationVarKey.CortexFacilityTimezone);
      const next = pickCascadedTimezone(all, scope);
      setResolved(next);
      setDraft(null);
      onValueChange?.(next.value);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, scope.kind, scope.groupId, scope.locationId]);

  if (!orgId) return null;

  const { ownerScope, ownerRow, value: savedValue, parentValue } = resolved;
  const ownsHere = ownerScope === scope.kind;
  const displayValue = draft ?? savedValue;

  const handleEdit = (): void => {
    setDraft(savedValue);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraft(null);
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    try {
      await API.LocationVars.createVariable(
        LocationVarKey.CortexFacilityTimezone,
        draft,
        orgId,
        scope.kind === "group" ? scope.groupId : undefined,
        scope.kind === "location" ? scope.locationId : undefined
      );
      await refresh();
      setIsEditing(false);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (): Promise<void> => {
    if (!ownerRow || !ownsHere) return;
    if (!window.confirm("Revert to Parent? This deletes this scope's timezone and falls back to the inherited one.")) return;
    setSaving(true);
    try {
      await API.LocationVars.deleteVariable(orgId, ownerRow.id);
      await refresh();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // Reverting is only meaningful for a row owned here, and only below org —
  // deleting the org row would leave nothing to inherit.
  const canRevert = ownsHere && scope.kind !== "org" && !!ownerRow;

  return (
    <ScheduleCard
      title="Timezone"
      caption="Used to work out whether this facility is open right now."
      // Timezone resolves through the variable cascade, so it has a real
      // VarDef default (America/New_York) beneath org — unlike the schedules.
      status={<InheritancePill source={ownerScope} currentScope={scope.kind} fallsBackToDefault />}
      error={error}
      loading={loading}
      loadingLabel="Loading timezone…"
      actions={
        isEditing ? (
          <>
            {canRevert && (
              <Tooltip content={`Reverts to: ${parentValue || "(not set)"}`}>
                <button
                  type="button"
                  onClick={handleRevert}
                  disabled={saving}
                  className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
                >
                  Revert to Parent
                  <GrRevert className="size-4" />
                </button>
              </Tooltip>
            )}
            <CardSaveActions saving={saving} onCancel={handleCancel} onSave={handleSave} />
          </>
        ) : (
          <CardEditAction onEdit={handleEdit} />
        )
      }
    >
      <div className="mx-4 grid grid-cols-[200px_minmax(0,1fr)] items-center gap-x-4 py-3">
        <span className="body-sm-alt text-grey-900">Timezone</span>
        <div className="min-w-0">
          {isEditing ? <TimezoneField value={displayValue} onChange={setDraft} /> : <p className="body-sm text-grey-900">{displayValue || "—"}</p>}
        </div>
      </div>
    </ScheduleCard>
  );
}

/**
 * Walk the (location > group > org) cascade and return the row that wins at
 * `scope`, where it was found, and what would win without it. Mirrors the
 * server-side `findCascaded` semantics so the UI matches the runtime.
 */
function pickCascadedTimezone(rows: LocationVarInterface[], scope: PageScope): ResolvedTimezone {
  const orgRow = rows.find((r) => r.locationId == null && r.groupId == null);
  const groupRow = scope.groupId != null ? rows.find((r) => r.locationId == null && r.groupId === scope.groupId) : undefined;
  const empty: ResolvedTimezone = { ownerScope: undefined, ownerRow: null, value: TIMEZONE_DEFAULT, parentValue: TIMEZONE_DEFAULT };

  if (scope.kind === "location") {
    const own = rows.find((r) => r.locationId === scope.locationId);
    const parentValue = groupRow?.value || orgRow?.value || TIMEZONE_DEFAULT;
    if (own?.value) return { ownerScope: "location", ownerRow: own, value: own.value, parentValue };
    if (groupRow?.value) return { ownerScope: "group", ownerRow: groupRow, value: groupRow.value, parentValue };
    if (orgRow?.value) return { ownerScope: "org", ownerRow: orgRow, value: orgRow.value, parentValue };
    return empty;
  }

  if (scope.kind === "group") {
    const parentValue = orgRow?.value || TIMEZONE_DEFAULT;
    if (groupRow?.value) return { ownerScope: "group", ownerRow: groupRow, value: groupRow.value, parentValue };
    if (orgRow?.value) return { ownerScope: "org", ownerRow: orgRow, value: orgRow.value, parentValue };
    return empty;
  }

  if (orgRow?.value) return { ownerScope: "org", ownerRow: orgRow, value: orgRow.value, parentValue: TIMEZONE_DEFAULT };
  return empty;
}
